import React, {useState} from 'react';
import {useTranslation} from "react-i18next";
import {SalaryUpdateButtonContainer, SalaryUpdateContainer} from "./styles";
import {isPositiveNumber, isValidString} from "../../../../utils";
import {useDispatch} from "react-redux";
import {Employee} from "../../../../app/interfaces";
import {
    InputFieldsWrapper,
    InputText,
    Modal, PopupAskForConfirmation,
    PrimaryButton,
    SecondaryButton,
    TextArea
} from '../../../../components';
import {modifyEmployeeSalary} from "../../../../app/employee/actions";

interface SalaryUpdateModalProps {
    employee: Employee,
    isOpen: boolean,
    onClose: () => void
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
interface Fields {
    amount: number | string;
    currency: string;
    reason: string;
}

export const SalaryUpdateModal = ({employee, isOpen, onClose}: SalaryUpdateModalProps) => {

    const {t} = useTranslation();
    const dispatch = useDispatch();

    const [reason, setReason] = useState<string>('');
    const [amount, setAmount] = useState<string>('');
    const [errors, setErrors] = useState<{ reason: string; amount: string }>({reason: '', amount: ''})
    const [showAskForConfirmation, setShowAskForConfirmation] = useState<boolean>(false)
    const handleReasonInput = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = event.target.value
        setReason(value);
        let errorMsg = '';
        if (!isValidString(value)) {
            errorMsg = t('enter_a_valid_description') as string
        }
        setErrors((e) => {
            return {
                ...e,
                reason: errorMsg
            }
        })
    }

    const handleAmountInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setAmount(value);
        let errorMsg = '';
        if (!isPositiveNumber(value)) {
            errorMsg = t('enter_a_valid_number') as string
        } else if (Number(value) === Number(employee.salary.amount)) {
            errorMsg = t('enter_a_new_salary') as string
        }
        setErrors((e) => {
            return {
                ...e,
                amount: errorMsg
            }
        })
    }

    const areFieldsFullFilled = () => {
        return !errors.reason && !errors.amount && isPositiveNumber(amount) && Number(amount) !== Number(employee.salary.amount) && isValidString(reason)
    }

    const resetForm = () => {
        setErrors({
            reason: '',
            amount: ''
        })
        setReason('')
        setAmount('')
    }

    const handleSubmit = () => {
        setShowAskForConfirmation(false);
        onClose();
        dispatch(modifyEmployeeSalary({
            employeeId: employee._id,
            companyId: employee.company._id,
            amount: Number(amount),
            reason: reason,
            currency: employee.salary.currency
        }) as any)
        resetForm();
    }

    return <Modal isOpen={isOpen}>
        <>
            {showAskForConfirmation && <PopupAskForConfirmation
                title={t('wait_a_minute') as string}
                subtitle={t('confirm_salary_update') as string}
                isOpen={showAskForConfirmation}
                onConfirm={handleSubmit}
                onCancel={() => setShowAskForConfirmation(false)}
                type='hand_stop'
            />}
            <SalaryUpdateContainer>
                <h1>{t('modify_gross_salary')}</h1>
                <p>{t('modify_salary_explanation')}</p>
                <InputFieldsWrapper>
                    <InputText
                        placeholder={t('enter_new_salary') as string}
                        label={t('gross_salary') as string}
                        value={amount}
                        status={errors.amount ? 'danger' : 'normal'}
                        helperText={errors.amount}
                        onChange={handleAmountInput}
                    />
                    {/* Keep always as EUROS */}
                    <InputText
                        readOnly
                        label={t('currency') as string}
                        value={employee.salary.currency}>
                    </InputText>
                </InputFieldsWrapper>
                <TextArea
                    value={reason}
                    label={t('reason') as string}
                    onChange={handleReasonInput}
                    status={errors.reason ? 'danger' : 'normal'}
                    helperText={errors.reason}
                />
                <SalaryUpdateButtonContainer>
                    <SecondaryButton onClick={onClose}>
                        {t('cancel')}
                    </SecondaryButton>
                    <PrimaryButton onClick={() => setShowAskForConfirmation(true)} disabled={!areFieldsFullFilled()}>
                        {t('submit')}
                    </PrimaryButton>
                </SalaryUpdateButtonContainer>
            </SalaryUpdateContainer>
        </>
    </Modal>
}
